const MILLI_PER_CREDIT = 1000;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Converts a milli-credit amount from the ledger into whole credits. */
export function toCredits(milliCredits: number): number {
  return milliCredits / MILLI_PER_CREDIT;
}

/**
 * Credits are metered in milli-credits, so every amount shown is exact to three
 * decimals rather than rounded: 0.75 reads as 0.75, never 0.8. Trailing zeros
 * drop, and there is no thousands grouping.
 */
function formatExactCredits(milliCredits: number, minimumFractionDigits: number): string {
  return toCredits(milliCredits).toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits: 3,
    useGrouping: false,
  });
}

/** Breakdown table rows keep the design's one decimal on whole amounts ("2.0"). */
export function formatBreakdownCredits(milliCredits: number): string {
  return formatExactCredits(milliCredits, 1);
}

/** Summary counts (hero, dropdown row, plan-card stats, table total). */
export function formatCreditCount(milliCredits: number): string {
  return formatExactCredits(milliCredits, 0);
}

export function formatUsd(amountUsd: number): string {
  return amountUsd.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * Renders a date as "9 Sep, 2026" from its **UTC** calendar fields.
 *
 * Every timestamp the credit ledger returns is a UTC instant, and the ones shown
 * here (billing period boundaries, grant `effectiveTo`) land exactly on UTC
 * midnight. Reading them in the viewer's local zone would resolve to the
 * previous calendar day for everyone west of UTC, so the UTC fields are read
 * directly rather than going through a locale-aware formatter.
 */
function formatUtcDate(date: Date): string {
  return `${date.getUTCDate()} ${SHORT_MONTHS[date.getUTCMonth()]}, ${date.getUTCFullYear()}`;
}

export function formatDate(isoDate: string): string {
  return formatUtcDate(new Date(isoDate));
}

/** `periodEnd` is exclusive, so the last day the period covers is the UTC day before it. */
export function formatPeriodEndingDate(periodEnd: string): string {
  return formatUtcDate(new Date(new Date(periodEnd).getTime() - MILLISECONDS_PER_DAY));
}
